/* @flow */
import React, {Component} from 'react';
import SimpleImageCarousel from '@homeaway/react-simple-image-carousel';
import {SVGDefinitions, SVGIcon} from '@homeaway/react-svg';
import {
    PHOTOCAROUSEL_LEFT_LABEL,
    PHOTOCAROUSEL_RIGHT_LABEL,
    triggerCarouselEDAPEvent,
    triggerCarouselImagePositionEdapEvent
} from '../../utils/carouselEdapUtils';
import './HitCarouselContainer.less';

import {type ListingImage} from '../Hit/Hit';

export type SimpleCarouselImage = {
    src: string,
    altText: string
};

type State = {
    images: SimpleCarouselImage[],
    imageIndex: number
};

type Props = {
    detailPageUrl: string,
    edapAction: string,
    images: ListingImage[],
    onImageClick?: Function
};

/**
 * SimpleImageCarousel uses a specific format {'src', 'altText'} so each ListingImage had to be converted into it first,
 * before the carousel can accept that data.
 * @param images Array of ListingImage
 * @returns an array of SimpleCarouselImage or empty array, if no images were initially provided.
 */
export const convertImagesIntoSimpleCarouselFormat = (
    images: ListingImage[]
): SimpleCarouselImage[] => {
    return images
        .filter((listingImage) => !!listingImage)
        .map((listingImage) => ({
            src: listingImage.c6_uri || '',
            altText: listingImage.altText || ''
        }));
};

/**
 * @homeaway/svg-defs v2.13 drastically reduced the size of the chevron icons,
 * breaking our carousel buttons. These are the definitions for the pre-2.13
 * chevrons so we can stay consistent until we find a design solution.
 */
export const HitCarouselIconDefinitions = () => (
    <SVGDefinitions>
        <symbol id="SVG_SERP_CHEVRON_LEFT__24">
            <svg
                data-id="SVG_SERP_CHEVRON_LEFT__24"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16 23L5 12 16 1"
                />
            </svg>
        </symbol>
        <symbol id="SVG_SERP_CHEVRON_RIGHT__24">
            <svg
                data-id="SVG_SERP_CHEVRON_RIGHT__24"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 23l11-11L8 1"
                />
            </svg>
        </symbol>
    </SVGDefinitions>
);

HitCarouselIconDefinitions.displayName = 'HitCarouselIconDefinitions';

export default class HitCarouselContainer extends Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            images: convertImagesIntoSimpleCarouselFormat(props.images),
            imageIndex: 0
        };
    }

    navigateToListing = () => {
        const {detailPageUrl, onImageClick} = this.props;
        if (onImageClick) {
            onImageClick();
        }
        // TODO: remove/refactor upon incorporating anchorHref functionality for carousels
        // see https://github.homeawaycorp.com/traveler/react-simple-image-carousel/issues/1
        // and https://github.homeawaycorp.com/traveler/react-image-carousel/issues/14
        // Why? - Back button will not close a new tab when using window.open
        window.open(detailPageUrl, '_blank');
    };

    onImageNavigate = (index: number) => {
        const {edapAction} = this.props;
        const {imageIndex: previousIndex, images} = this.state;

        this.setState({imageIndex: index});
        const imageSrc = images[index].src;

        const edapLabel =
            (previousIndex + 1 === images.length && index === 0) ||
            previousIndex + 1 === index
                ? PHOTOCAROUSEL_RIGHT_LABEL
                : PHOTOCAROUSEL_LEFT_LABEL;

        triggerCarouselEDAPEvent(edapAction, edapLabel);

        if (imageSrc) {
            triggerCarouselImagePositionEdapEvent(`${imageSrc}:${index}`);
        }
    };

    render() {
        return (
            <SimpleImageCarousel
                images={this.state.images}
                index={this.state.imageIndex}
                transitionStyle="slide"
                transitionDuration={250}
                onNextClick={this.onImageNavigate}
                onPrevClick={this.onImageNavigate}
                onClick={this.navigateToListing}
                tabIndex={-1}
                // Lifted from simple-image-carousel, with icons replaced with
                // non-baseline SERP specials.
                //
                // TODO: Remove once this issue is fixed in
                // simple-image-carousel
                renderButtons={({setPrevRef, setNextRef}) => {
                    return [
                        <button
                            key="0"
                            className="SimpleImageCarousel__button SimpleImageCarousel__button--left"
                            data-wdio="SimpleImageCarousel__button--prev"
                            ref={setPrevRef}
                            tabIndex="-1"
                        >
                            <span className="sr-only">{'Previous Image'}</span>
                            <SVGIcon
                                className="SimpleImageCarousel__svg SimpleImageCarousel__svg--left"
                                id="SERP_CHEVRON_LEFT__24"
                            />
                        </button>,
                        <button
                            key="1"
                            className="SimpleImageCarousel__button SimpleImageCarousel__button--right"
                            data-wdio="SimpleImageCarousel__button--next"
                            ref={setNextRef}
                            tabIndex="-1"
                        >
                            <span className="sr-only">{'Next Image'}</span>
                            <SVGIcon
                                className="SimpleImageCarousel__svg SimpleImageCarousel__svg--right"
                                id="SERP_CHEVRON_RIGHT__24"
                            />
                        </button>
                    ];
                }}
            />
        );
    }
}
